/**
 * Build-time feature switches.
 *
 * ENFORCEMENT_REPORTS: the police-type quick action and the Vehicles module
 * (sightings, plates, photos). On for the web app. Off for the App Store
 * build (`npm run build:native`), because Apple removed ICE-sighting apps in
 * October 2025 and the developer account is shared with every other app.
 * With it off, nothing police- or plate-shaped appears anywhere in the UI:
 * such reports arriving from web users show as a neutral "Alert", with no
 * plate, photo or vehicle detail.
 */

import { QUICK_ACTIONS, type QuickAction, type QuickActionKey } from './types'

export const ENFORCEMENT_REPORTS: boolean = import.meta.env.VITE_FEATURE_ENFORCEMENT_REPORTS !== 'false'

const ENFORCEMENT_KEYS: ReadonlySet<QuickActionKey> = new Set<QuickActionKey>(['police', 'vehicle_sighting'])

const NEUTRAL_ALERT: Omit<QuickAction, 'key'> = {
  emoji: '⚠️',
  label: 'Alert',
  color: '#eab308',
  message: 'Alert reported here',
}

export function isEnforcementAction(key: QuickActionKey, enabled = ENFORCEMENT_REPORTS): boolean {
  return !enabled && ENFORCEMENT_KEYS.has(key)
}

/** The quick actions a person may tap in this build. */
export function quickActionsFor(enabled = ENFORCEMENT_REPORTS): QuickAction[] {
  return Object.values(QUICK_ACTIONS).filter((a) => enabled || !ENFORCEMENT_KEYS.has(a.key))
}

/** How to SHOW a report of this type in this build. */
export function displayAction(key: QuickActionKey, enabled = ENFORCEMENT_REPORTS): QuickAction {
  const action = QUICK_ACTIONS[key]
  return isEnforcementAction(key, enabled) ? { ...action, ...NEUTRAL_ALERT } : action
}
